import React from 'react';
import { getIn, useFormikContext } from 'formik';

import TextFieldFormik from '~/components/TextFieldFormik';

import GbgSuburbAutocomplete from './GbgSuburbAutocomplete';
import type { GbgSuburbSuggestion } from './types';
import { isGbgSupportedCountry, useGbgValidationStatus } from './useGbgValidationStatus';

interface SuburbAutocompleteFormikProps {
  id: string;
  cityFieldId: string;
  zipcodeFieldId: string;
  streetAddress2FieldId: string;
  stateFieldId: string;
  countryFieldId: string;
  label: string;
  disabled?: boolean;
  fullWidth?: boolean;
}

const SuburbAutocompleteFormik = ({
  id,
  cityFieldId,
  zipcodeFieldId,
  streetAddress2FieldId,
  stateFieldId,
  countryFieldId,
  label,
  disabled = false,
  fullWidth = false,
}: SuburbAutocompleteFormikProps) => {
  const { setFieldValue, validateForm, values, setFieldTouched, errors, touched } = useFormikContext();
  const gbgValidationStatus = useGbgValidationStatus();

  const country = getIn(values, countryFieldId);
  const isGbgEnabled = gbgValidationStatus.is_address_validation_enabled && isGbgSupportedCountry(country);

  if (!isGbgEnabled) {
    return <TextFieldFormik id={id} label={label} fullWidth={fullWidth} disabled={disabled} />;
  }

  const handleSuburbSelected = (suggestion: GbgSuburbSuggestion) => {
    // MUI's onInputChange fires synchronously after onChange with full_address,
    // overwriting the field. Defer corrections so they run after that cycle —
    // same pattern as the address autocomplete where the async retrieve call
    // naturally provides this delay.
    queueMicrotask(async () => {
      await Promise.all([
        setFieldValue(cityFieldId, suggestion.city, false),
        setFieldValue(zipcodeFieldId, suggestion.postcode, false),
        setFieldValue(stateFieldId, suggestion.state, false),
        // Only write street_address2 when the suggestion provides one (NZ suburbs).
        // Intentionally not clearing it otherwise — the user may have manually
        // entered a unit/apartment value that should be preserved.
        // Trade-off: if a user selects an NZ suburb that has a street_address2
        // and then selects another that doesn't, the previous value remains stale.
        suggestion.street_address2
          ? setFieldValue(streetAddress2FieldId, suggestion.street_address2, false)
          : Promise.resolve(),
      ]);
      validateForm();
    });
  };

  return (
    <GbgSuburbAutocomplete
      id={id}
      label={label}
      country={country}
      disabled={disabled}
      value={getIn(values, id)}
      error={getIn(touched, id) && getIn(errors, id) ? getIn(errors, id) : undefined}
      onSuburbSelected={handleSuburbSelected}
      onInputChange={(_: React.SyntheticEvent | null, value: string) => setFieldValue(id, value)}
      onBlur={() => setFieldTouched(id, true)}
    />
  );
};

export default SuburbAutocompleteFormik;
